import ReactDOM from "react-dom/client";
import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import useAdmissionChallanPdf from "../../hooks/useAdmissionChallanPdf";
import FullScreenLoader from "../../components/FullScreenLoader";
import { useParams } from "react-router-dom";

function RegistrationCheck({ onRegistrationSubmit }) {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    trigger,
    reset,
    getValues,
  } = useForm();

  const { registrationNoFromUrl } = useParams();

  if (registrationNoFromUrl) {
    setValue("registrationNo", registrationNoFromUrl);
  }

  useEffect(() => {
    fetchFirstRegistrationData();
  }, [registrationNoFromUrl]);

  const fetchFirstRegistrationData = async () => {
    const registrationNo = getValues("registrationNo");

    if (!registrationNo) {
      setError("registrationNo", {
        type: "custom",
        message: "This field is required.",
      });
      return;
    }

    setChallanAlreadyGenerated(false);
    setRegNo(registrationNo);

    try {
      const response = await get(
        `Registration/GetRegistrationByRegNo?registrationNo=${registrationNo}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { result, registration } = response;

      if (!result && !registration) {
        toast.error("No data found for this Registration Number.");
        reset({
          registrationNo: getValues("registrationNo"),
          firstName: "",
          lastName: "",
          dateOfBirth: "",
          gender: "",
          nationality: "",
          religion: "",
          guardianName: "",
          relation: "",
          occupation: "",
          guardianIdentityNo: "",
          mobileNo: "",
          email: "",
          countryId: "",
          stateId: "",
          cityId: "",
          centerId: "",
          centerClassId: "",
          sourceOfInformation: "",
          grandParentsName: "",
          code: "",
          id: 0,
        });
        return;
      }

      if (result) {
        setChallanAlreadyGenerated(true);
        toast.info("Admission challan already generated.");
        reset({
          registrationNo: getValues("registrationNo"),
          firstName: "",
          lastName: "",
          dateOfBirth: "",
          gender: "",
          nationality: "",
          religion: "",
          guardianName: "",
          relation: "",
          occupation: "",
          guardianIdentityNo: "",
          mobileNo: "",
          email: "",
          countryId: "",
          stateId: "",
          cityId: "",
          centerId: "",
          centerClassId: "",
          sourceOfInformation: "",
          grandParentsName: "",
          code: "",
          id: 0,
        });
        return;
      }

      setFetchedCenterId(registration?.centerId);
      setFetchedClassId(registration?.centerClassId);

      // Fetch states based on the organization's countryId
      const countryId = registration?.countryId;
      const stateId = registration?.stateId;
      const cityId = registration?.cityId;
      const centerId = registration?.centerId;
      const centerClassId = registration?.centerClassId;

      let selectedCountry = null;
      let selectedState = null;
      let selectedCity = null;
      let selectedCenter = null;
      let selectedClass = null;

      const countriesResponse = await get("Dropdown/GetCountries", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCountries(countriesResponse);

      selectedCountry = countriesResponse.find(
        (country) => country.id === countryId
      );

      if (countryId) {
        const statesResponse = await get(
          `Dropdown/GetStates?countryId=${countryId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setStates(statesResponse);

        if (stateId) {
          selectedState = statesResponse.find((state) => state.id === stateId);
          const citiesResponse = await get(
            `Dropdown/GetCities?stateId=${stateId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setCities(citiesResponse);

          if (cityId) {
            selectedCity = citiesResponse.find((city) => city.id === cityId);
            const centersResponse = await get(
              `Center/GetCenterByCityId?cityId=${cityId}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            setCentersData(centersResponse?.center);

            if (centerId) {
              selectedCenter = centersResponse?.center.find(
                (center) => center.id === centerId
              );
              const classesResponse = await get(
                `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );
              setClassesData(classesResponse);

              if (centerClassId) {
                selectedClass = classesResponse.find(
                  (classItem) => classItem.id === centerClassId
                );
              }
            }
          }
        }
      }

      const selectedGender = genders.find(
        (option) => option.id === registration?.gender
      );
      const selectedRelation = studentRelations.find(
        (option) => option.id === registration?.relation
      );
      const selectedOccupation = guardianOccupations.find(
        (option) => option.id === registration?.occupation
      );
      const selectedSourceOfInfo = sourceOfInfoOptions.find(
        (option) => option.id === registration?.sourceOfInformation
      );

      reset({
        id: registration?.id,
        firstName: registration?.firstName,
        lastName: registration?.lastName,
        dateOfBirth: registration?.dateOfBirth?.split("T")[0],
        gender: selectedGender
          ? { value: selectedGender.id, label: selectedGender.name }
          : null,
        nationality: registration?.nationality,
        religion: registration?.religion,
        countryId: { id: selectedCountry.id, label: selectedCountry.name },
        stateId: { id: selectedState.id, label: selectedState.name },
        cityId: { id: selectedCity.id, label: selectedCity.name },
        centerId: { id: selectedCenter.id, label: selectedCenter.name },
        centerClassId: { id: selectedClass.id, label: selectedClass.name },
        guardianName: registration?.guardianName,
        relation: selectedRelation
          ? { value: selectedRelation.id, label: selectedRelation.name }
          : null,
        occupation: selectedOccupation
          ? { value: selectedOccupation.id, label: selectedOccupation.name }
          : null,
        guardianIdentityNo: registration?.guardianIdentityNo,
        mobileNo: registration?.mobileNo,
        email: registration?.email,
        sourceOfInformation: selectedSourceOfInfo
          ? { value: selectedSourceOfInfo.id, label: selectedSourceOfInfo.name }
          : null,
        code: registration?.code,
      });
      setChallanAlreadyGenerated(false);
    } catch (error) {
      console.error("Error fetching first reg data: ", error);
    }
  };

  const [fetchedCenterId, setFetchedCenterId] = useState(0);
  const [fetchedClassId, setFetchedClassId] = useState(0);
  const [regNo, setRegNo] = useState("");

  const [challanAlreadyGenerated, setChallanAlreadyGenerated] = useState(false);

  const { generatePDF, loading } = useAdmissionChallanPdf();

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [centersData, setCentersData] = useState([]);
  const [classesData, setClassesData] = useState([]);

  const selectedCountry = watch("countryId");
  const selectedState = watch("stateId");
  const selectedCity = watch("cityId");
  const selectedCenter = watch("centerId");

  const [sessionsData, setSessionsData] = useState([]);

  const [languagesData, setLanguagesData] = useState([]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await get("Dropdown/GetCountries", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setCountries(response);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCountries();
  }, [token]);

  useEffect(() => {
    if (selectedCountry && selectedCountry.value) {
      const fetchStates = async () => {
        try {
          const response = await get(
            `Dropdown/GetStates?countryId=${selectedCountry.value}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setStates(response);

          setCities([]);
          setValue("stateId", null);
          setValue("cityId", null);
        } catch (error) {
          console.error("Error fetching states:", error);
        }
      };

      fetchStates();
    }
  }, [selectedCountry, setValue, token]);

  useEffect(() => {
    if (selectedState && selectedState.value) {
      const fetchCities = async () => {
        try {
          const response = await get(
            `Dropdown/GetCities?stateId=${selectedState.value}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setCities(response);

          setValue("cityId", null);
        } catch (error) {
          console.error("Error fetching cities:", error);
        }
      };

      fetchCities();
    }
  }, [selectedState, setValue, token]);

  useEffect(() => {
    if (selectedCity && selectedCity.value) {
      const fetchCenters = async () => {
        try {
          const response = await get(
            `Center/GetCenterByCityId?cityId=${selectedCity.value}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          setCentersData(response.center);
        } catch (error) {
          console.error("Error fetching countries:", error);
        }
      };

      fetchCenters();
    }
  }, [selectedCity, setValue, watch]);

  useEffect(() => {
    if (selectedCenter && selectedCenter.value) {
      const fetchClasses = async () => {
        try {
          const response = await get(
            `CenterClass/GetClassCenterByCenterId?id=${selectedCenter.value}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          setClassesData(response);
        } catch (error) {
          console.error("Error fetching countries:", error);
        }
      };

      fetchClasses();
    }
  }, [selectedCenter, setValue, watch]);

  //

  useEffect(() => {
    const getSessionsList = async () => {
      if (!token) {
        console.error("Unauthorized.");
        return;
      }

      try {
        const response = await get(
          `Session/GetSessions?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSessionsData(response.sessions);
      } catch (error) {
        console.error("Error fetching sessions:", error);
      }
    };

    getSessionsList();
  }, [setValue, watch]);

  useEffect(() => {
    const getLanguagesList = async () => {
      if (!token) {
        console.error("Unauthorized.");
        return;
      }

      try {
        const response = await get(`Language/GetAllLanguageAsync`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setLanguagesData(response);
      } catch (error) {
        console.error("Error fetching sessions:", error);
      }
    };

    getLanguagesList();
  }, [setValue, watch]);

  const genders = [
    {
      id: 1,
      name: "Male",
    },
    {
      id: 2,
      name: "Female",
    },
  ];

  const studentRelations = [
    {
      id: "Father",
      name: "Father",
    },
    {
      id: "Mother",
      name: "Mother",
    },
    {
      id: "Brother",
      name: "Brother",
    },
    {
      id: "Sister",
      name: "Sister",
    },
    {
      id: "Uncle",
      name: "Uncle",
    },
    {
      id: "Guardian",
      name: "Guardian",
    },
  ];

  const guardianOccupations = [
    {
      id: "Business",
      name: "Business",
    },
    {
      id: "Employed",
      name: "Employed",
    },
  ];

  const sourceOfInfoOptions = [
    {
      id: "Social Media",
      name: "Social Media",
    },
    {
      id: "Newspaper Ad",
      name: "Newspaper Ad",
    },
    {
      id: "Word of Mouth",
      name: "Word of Mouth",
    },
    {
      id: "Campus Visit",
      name: "Campus Visit",
    },
  ];

  // useEffect(() => {
  //   const checkFeePlanAssignment = async () => {
  //     try {
  //       const response = await get(
  //         `AssessmentType/GetAdmissionVoucherByRegistartionNoAsync?registartionNo=${registrationNoFromUrl}`,
  //         {
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //           },
  //         }
  //       );

  //       if (response?.admissionVoucher.length === 0) {
  //         alert("Please assign fee plan to this center");
  //       }
  //     } catch (error) {
  //       console.error("Error fetching challan data:", error);
  //       throw error;
  //     }
  //   };

  //   checkFeePlanAssignment();
  // }, []);

  const handlePrintAdmissionChallanAction = async () => {
    if (!regNo) {
      alert("Please enter registration number.");
      return;
    }

    await generatePDF(regNo);
  };

  const saveData = async (data) => {
    const formData = {
      ...data,
      countryId: data?.countryId?.value || data?.countryId?.id,
      stateId: data?.stateId?.value || data?.stateId?.id,
      cityId: data?.cityId?.value || data?.cityId?.id,
      centerId: data?.centerId?.value || data?.centerId?.id,
      centerClassId: data?.centerClassId?.value || data?.centerClassId?.id,
      gender: data?.gender?.value,
      occupation: data?.occupation?.value,
      relation: data?.relation?.value,
      sourceOfInformation: data?.sourceOfInformation?.value || "",
      grandParentsName: "",
    };

    try {
      const response = await post("Registration", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.isSuccess) {
        onRegistrationSubmit(fetchedCenterId, fetchedClassId, regNo);
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  return (
    <>
      <div className="card">
        <div className="card-body">
          {/* <h2 className="fs-8 text-secondary">Student First Registration</h2> */}
          <form className="row g-3" onSubmit={handleSubmit(saveData)}>
            <div className="col-12">
              <div className="row justify-content-center">
                <div className="col-md-5">
                  <label className="form-label ms-3">
                    Registration Number
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <div className="reg-check-wrapper">
                    <div className="reg-input">
                      <input
                        {...register(`registrationNo`, {
                          required: "This field is required.",
                        })}
                        type="text"
                        className="form-control"
                        placeholder="Enter Registration Number"
                        onBlur={fetchFirstRegistrationData}
                        disabled={true}
                      />
                      {errors.registrationNo && (
                        <small className="text-danger ms-3">
                          {errors.registrationNo.message}
                        </small>
                      )}
                    </div>
                    <div className="reg-check-btn">
                      {/* <button
                        className="btn btn-primary rounded-circle"
                        type="button"
                        onClick={fetchFirstRegistrationData}
                      >
                        <i className="fa fa-search"></i>
                      </button> */}
                      {challanAlreadyGenerated && (
                        <button
                          className="btn btn-primary rounded-circle ms-1"
                          type="button"
                          onClick={handlePrintAdmissionChallanAction}
                        >
                          <i className="fa fa-print"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3">
              <h5 className="mb-0 p-2 px-3 mt-4 text-secondary bg-brand-light rounded-pill">
                Admission Information
              </h5>

              <div className="col-12">
                <div className="row g-3">
                  <div className="col">
                    <label className="form-label">
                      Country<span className="text-danger fs-9">*</span>
                    </label>

                    <Controller
                      name={`countryId`}
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={countries.map((country) => ({
                            value: country.id,
                            label: country.name,
                          }))}
                          onChange={(selectedOption, actionMeta) => {
                            if (actionMeta.action === "clear") {
                              setValue(`countryId`, null);
                            } else {
                              setValue(
                                `countryId`,
                                selectedOption || { value: 0, label: "" }
                              );
                            }
                            trigger(`countryId`);
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          isDisabled={true}
                        />
                      )}
                    />
                    {errors.countryId && (
                      <small className="text-danger">
                        {errors.countryId.message}
                      </small>
                    )}
                  </div>
                  <div className="col">
                    <label className="form-label">
                      State<span className="text-danger fs-9">*</span>
                    </label>

                    <Controller
                      name={`stateId`}
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={states.map((state) => ({
                            value: state.id,
                            label: state.name,
                          }))}
                          onChange={(selectedOption, actionMeta) => {
                            if (actionMeta.action === "clear") {
                              setValue(`stateId`, null);
                            } else {
                              setValue(
                                `stateId`,
                                selectedOption || { value: 0, label: "" }
                              );
                            }
                            trigger(`stateId`);
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          isDisabled={true}
                        />
                      )}
                    />
                    {errors.stateId && (
                      <small className="text-danger">
                        {errors.stateId.message}
                      </small>
                    )}
                  </div>
                  <div className="col">
                    <label className="form-label">
                      City<span className="text-danger fs-9">*</span>
                    </label>

                    <Controller
                      name={`cityId`}
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={cities.map((city) => ({
                            value: city.id,
                            label: city.name,
                          }))}
                          onChange={(selectedOption, actionMeta) => {
                            if (actionMeta.action === "clear") {
                              setValue(`cityId`, null);
                            } else {
                              setValue(
                                `cityId`,
                                selectedOption || { value: 0, label: "" }
                              );
                            }
                            trigger(`cityId`);
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          isDisabled={true}
                        />
                      )}
                    />
                    {errors.cityId && (
                      <small className="text-danger">
                        {errors.cityId.message}
                      </small>
                    )}
                  </div>
                  <div className="col">
                    <label className="form-label">
                      Center<span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name={`centerId`}
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={centersData.map((center) => ({
                            value: center.id,
                            label: center.name,
                          }))}
                          onChange={(selectedOption, actionMeta) => {
                            if (actionMeta.action === "create-option") {
                              setValue(`centerId`, {
                                value: 0,
                                label: selectedOption.label,
                              });
                            } else if (actionMeta.action === "clear") {
                              setValue(`centerId`, null);
                              setValue(`centerClassId`, null);
                            } else {
                              setValue(
                                `centerId`,
                                selectedOption || { value: 0, label: "" }
                              );
                              setValue(`centerClassId`, null);
                            }
                            trigger(`centerId`);
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          // isDisabled={true}
                        />
                      )}
                    />
                    {errors.centerId && (
                      <small className="text-danger">
                        {errors.centerId.message}
                      </small>
                    )}
                  </div>
                  <div className="col">
                    <label className="form-label">
                      Class<span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name={`centerClassId`}
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={classesData.map((classItem) => ({
                            value: classItem.id,
                            label: classItem.name,
                          }))}
                          onChange={(selectedOption, actionMeta) => {
                            if (actionMeta.action === "create-option") {
                              setValue(`centerClassId`, {
                                value: 0,
                                label: selectedOption.label,
                              });
                            } else if (actionMeta.action === "clear") {
                              setValue(`centerClassId`, null);
                            } else {
                              setValue(
                                `centerClassId`,
                                selectedOption || { value: 0, label: "" }
                              );
                            }
                            trigger(`centerClassId`);
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          // isDisabled={true}
                        />
                      )}
                    />
                    {errors.centerClassId && (
                      <small className="text-danger">
                        {errors.centerClassId.message}
                      </small>
                    )}
                  </div>
                </div>
              </div>

              <h5 className="mb-0 p-2 px-3 mt-4 text-secondary bg-brand-light rounded-pill">
                Student Information
              </h5>

              <div className="col-12">
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">
                      First Name
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("firstName", {
                        required: "This field is required.",
                      })}
                      type="text"
                      className="form-control"
                      placeholder=""
                    />
                    {errors.firstName && (
                      <small className="text-danger">
                        {errors.firstName.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">
                      Last Name<span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("lastName", {
                        required: "This field is required.",
                      })}
                      type="text"
                      className="form-control"
                      placeholder=""
                    />
                    {errors.lastName && (
                      <small className="text-danger">
                        {errors.lastName.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">
                      Date of Birth
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register(`dateOfBirth`, {
                        required: "This field is required.",
                      })}
                      type="date"
                      className="form-control"
                      placeholder=""
                    />
                    {errors.dateOfBirth && (
                      <small className="text-danger">
                        {errors.dateOfBirth.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">
                      Gender<span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name="gender"
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <select
                          {...field}
                          className="form-select"
                          value={field.value || ""}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        >
                          <option value="" disabled key={"gender-key"}>
                            Select
                          </option>
                          {genders.map((gender) => (
                            <option key={gender.id} value={gender.id}>
                              {gender.name}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                    {errors.gender && (
                      <small className="text-danger">
                        {errors.gender.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">
                      Nationality<span className="text-danger fs-9">*</span>
                    </label>
                    <select
                      {...register("nationality", {
                        required: "This field is required.",
                      })}
                      className="form-select"
                    >
                      <option key={0} value="">
                        Select
                      </option>
                      <option key="Pakistani" value="Pakistani">
                        Pakistani
                      </option>
                      <option key="Australian" value="Australian">
                        Australian
                      </option>
                    </select>
                    {errors.nationality && (
                      <small className="text-danger">
                        {errors.nationality.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">
                      Religion<span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("religion", {
                        required: "This field is required.",
                      })}
                      type="text"
                      className="form-control"
                      placeholder=""
                    />
                    {errors.religion && (
                      <small className="text-danger">
                        {errors.religion.message}
                      </small>
                    )}
                  </div>
                </div>
              </div>

              <h5 className="mb-0 p-2 px-3 mt-4 text-secondary bg-brand-light rounded-pill">
                Guardian Information
              </h5>

              <div className="col-12">
                <div className="row g-3">
                  <div className="col-sm-3">
                    <label className="form-label">
                      Name<span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("guardianName", {
                        required: "This field is required.",
                      })}
                      type="text"
                      className="form-control"
                    />
                    {errors.guardianName && (
                      <small className="text-danger fs-9">
                        {errors.guardianName.message}
                      </small>
                    )}
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label">
                      Relation
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name={`relation`}
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={studentRelations.map((relation) => ({
                            value: relation.id,
                            label: relation.name,
                          }))}
                          onChange={(selectedOption, actionMeta) => {
                            if (actionMeta.action === "clear") {
                              setValue(`relation`, null);
                            } else {
                              setValue(
                                `relation`,
                                selectedOption || { value: 0, label: "" }
                              );
                            }
                            trigger(`relation`);
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          // isClearable
                        />
                      )}
                    />
                    {errors.relation && (
                      <small className="text-danger fs-9">
                        {errors.relation.message}
                      </small>
                    )}
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label">
                      Identity Number
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("guardianIdentityNo", {
                        required: "This field is required.",
                      })}
                      type="text"
                      className="form-control"
                    />
                    {errors.guardianIdentityNo && (
                      <small className="text-danger fs-9">
                        {errors.guardianIdentityNo.message}
                      </small>
                    )}
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label">
                      Mobile Number
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("mobileNo", {
                        required: "This field is required.",
                      })}
                      type="text"
                      className="form-control"
                    />
                    {errors.mobileNo && (
                      <small className="text-danger fs-9">
                        {errors.mobileNo.message}
                      </small>
                    )}
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label">
                      Email Address
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("email", {
                        required: "This field is required.",
                      })}
                      type="text"
                      className="form-control"
                    />
                    {errors.email && (
                      <small className="text-danger fs-9">
                        {errors.email.message}
                      </small>
                    )}
                  </div>
                  <div className="col-sm-3">
                    <label className="form-label">
                      Occupation
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name={`occupation`}
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={guardianOccupations.map((occupation) => ({
                            value: occupation.id,
                            label: occupation.name,
                          }))}
                          onChange={(selectedOption, actionMeta) => {
                            if (actionMeta.action === "clear") {
                              setValue(`occupation`, null);
                            } else {
                              setValue(
                                `occupation`,
                                selectedOption || { value: 0, label: "" }
                              );
                            }
                            trigger(`occupation`);
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          // isClearable
                        />
                      )}
                    />
                    {errors.occupation && (
                      <small className="text-danger fs-9">
                        {errors.occupation.message}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12">
              <button
                className="btn btn-primary btn-sm"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Next" : "Next"}
              </button>
            </div>
          </form>
        </div>
      </div>
      {loading && <FullScreenLoader />}
    </>
  );
}

export default RegistrationCheck;
